import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';

/* Chakra v3 system — "graphite + racing red" brand (brand/tt-graphite-red.html),
   the same palette as the Android redesign (docs/17-ANDROID-REDESIGN-AUDIT.md
   §19.1). Values here MUST stay in sync with src/shared/ui/tokens.css (the
   legacy mini-kit layer read directly by a handful of non-Chakra components,
   see docs/22-WEB-ADMIN-REDESIGN-AUDIT.md §1.3) — drifting one without the
   other reproduces the theme-collision bug already caught once on the
   Android HTML prototype (see docs/23-WEB-ADMIN-REDESIGN-IMPLEMENTATION-PLAN.md §0.2). */

const config = defineConfig({
  // Brand is racing red now (was teal). Default color palette so primary
  // buttons/badges/focus rings inherit it instead of Chakra's neutral gray.
  globalCss: {
    html: { colorPalette: 'brand' },
  },
  theme: {
    breakpoints: {
      sm: '30em',
      md: '48em',
      lg: '62em',
      xl: '80em',
      '2xl': '96em',
      '3xl': '103em',
    },
    recipes: {
      // Uber-esque hierarchy is weight-driven, not just size (§19.2):
      // Chakra's default Heading is "semibold" (600) everywhere. Bumping
      // the recipe base to 800 (matches the Android h1/display weight)
      // is a single, low-risk lever — it doesn't touch any size/spacing
      // token, so it can't disturb layout on any of the 22 pages.
      heading: {
        base: {
          fontWeight: 'extrabold',
        },
      },
    },
    tokens: {
      colors: {
        // Racing red ramp — only `brand.500`/`.600` correspond to a
        // literal documented hex (light accent); the rest are a
        // generated tint/shade scale for Chakra's family-shaped
        // semantic slots (subtle/muted/emphasized/fg below).
        brand: {
          50: { value: '#fef2f2' },
          100: { value: '#fde3e4' },
          200: { value: '#fbc8cb' },
          300: { value: '#f7a0a6' },
          400: { value: '#f17179' },
          500: { value: '#e63946' },
          600: { value: '#d42a38' },
          700: { value: '#b31e2b' },
          800: { value: '#8f1b26' },
          900: { value: '#741a22' },
          950: { value: '#400b0f' },
        },
        // Warm graphite neutral — replaces Chakra's cool default gray so
        // every incidental `gray.*`/`colorPalette="gray"` usage reads
        // warm, matching the surface family below rather than clinical
        // Tailwind zinc. The primary bg/fg/border tokens are overridden
        // explicitly further down for pixel-exact brand values; this
        // ramp is the fallback for anything referencing gray directly.
        gray: {
          50: { value: '#fafaf9' },
          100: { value: '#f1efea' },
          200: { value: '#e4e2dd' },
          300: { value: '#d3d0c9' },
          400: { value: '#a8a9ac' },
          500: { value: '#7c828c' },
          600: { value: '#5b6472' },
          700: { value: '#3d4551' },
          800: { value: '#232936' },
          900: { value: '#1b2029' },
          950: { value: '#12151b' },
        },
      },
      // Component recipes (Button/Input/Badge → l2, Menu → l1/l2,
      // Dialog/Popover/Drawer/Card → l3) all read these three semantic
      // steps for their corner radius — raising them here is what turns
      // Chakra's default sharp-ish 4-6px corners into the rounded
      // Uber-esque 8/12/16px scale from §19.3, everywhere, at once.
      radii: {
        l1: { value: '8px' },
        l2: { value: '12px' },
        l3: { value: '16px' },
      },
      fonts: {
        // Deliberate system sans stack (matches Android §19.2): reliable
        // Cyrillic everywhere, no webfont weight. Chakra's own default
        // token is `Inter, <fallback>` and Inter was never actually
        // loaded here either (no @font-face, CSP blocks font CDNs) — this
        // makes the existing fallback behavior explicit instead of silent,
        // same fix as tokens.css.
        heading: {
          value:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        },
        body: {
          value:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        },
      },
    },
    semanticTokens: {
      colors: {
        // Same 8-key shape Chakra uses for its own built-in palettes
        // (dist/esm/theme/semantic-tokens/colors.js) so `colorPalette`
        // (defaulted to "brand" above) drives solid buttons/badges/focus
        // rings correctly. `solid`/`focusRing`/`subtle`/`contrast` pin to
        // the literal documented accent/accent-soft/accent-ink hex
        // (§19.1) rather than the generated ramp, since those are the
        // highest-visibility, must-match-Android tokens; the rest use the
        // ramp the same way Chakra's own `red`/`teal` blocks do.
        brand: {
          contrast: { value: { _light: '#ffffff', _dark: '#ffffff' } },
          fg: { value: { _light: '{colors.brand.700}', _dark: '{colors.brand.300}' } },
          subtle: { value: { _light: '#fbe4e5', _dark: 'rgba(255, 77, 94, 0.16)' } },
          muted: { value: { _light: '{colors.brand.200}', _dark: '{colors.brand.800}' } },
          emphasized: { value: { _light: '{colors.brand.300}', _dark: '{colors.brand.700}' } },
          solid: { value: { _light: '#e63946', _dark: '#ff4d5e' } },
          focusRing: { value: { _light: '#e63946', _dark: '#ff4d5e' } },
          border: { value: { _light: '{colors.brand.500}', _dark: '{colors.brand.400}' } },
        },
        // Literal overrides for the app canvas/panel/text/border tokens —
        // more precise than leaning on the gray ramp above, since our
        // light and dark surfaces aren't a single continuous hue (warm
        // cream bg in light, cool graphite in dark) the way a generic
        // scale assumes. Same values as tokens.css §Light/Dark surfaces.
        bg: {
          DEFAULT: { value: { _light: '#f6f5f3', _dark: '#12151b' } },
          panel: { value: { _light: '#ffffff', _dark: '#1b2029' } },
          subtle: { value: { _light: '#efeeeb', _dark: '#232936' } },
          muted: { value: { _light: '#efeeeb', _dark: '#232936' } },
          emphasized: { value: { _light: '#e4e2dd', _dark: '#2a303c' } },
          inverted: { value: { _light: '#1f2937', _dark: '#f3f1ec' } },
        },
        fg: {
          DEFAULT: { value: { _light: '#1f2937', _dark: '#f3f1ec' } },
          muted: { value: { _light: '#5b6472', _dark: '#98a0ac' } },
          subtle: { value: { _light: '#9aa2ad', _dark: '#5b6472' } },
          inverted: { value: { _light: '#f3f1ec', _dark: '#1f2937' } },
        },
        border: {
          DEFAULT: { value: { _light: '#e4e2dd', _dark: '#2a303c' } },
          muted: { value: { _light: '#efeeeb', _dark: '#232936' } },
          subtle: { value: { _light: '#f1efea', _dark: '#1b2029' } },
          emphasized: { value: { _light: '#d8d5cf', _dark: '#3a4150' } },
        },
      },
    },
  },
});

export const system = createSystem(defaultConfig, config);
